import * as React from 'react';
import { Link } from '@tanstack/react-router';

import { cn } from '@/lib/cn';

/**
 * AuthLayout — wraps the centered-card auth pages (login, operator login,
 * set-password, team accept) with a consistent brand strip + subtle
 * radial-gradient background. Replaces the previous `<div className="grid
 * min-h-screen place-items-center bg-surface p-6">…<Card>…</Card></div>`
 * pattern across 4 routes.
 *
 * Layout: brand wordmark anchored top-left (links home on marketing site),
 * card centered vertically, tagline + small-print anchored bottom-center.
 */
export interface AuthLayoutProps {
  children: React.ReactNode;
  /** Bottom-anchored small print. Defaults to legal footer line. */
  smallPrint?: React.ReactNode;
  className?: string;
}

export function AuthLayout({ children, smallPrint, className }: AuthLayoutProps) {
  return (
    <div
      className={cn(
        'relative grid min-h-screen grid-rows-[auto_1fr_auto] overflow-hidden bg-surface',
        className
      )}
      style={{
        // Subtle two-tone radial wash so the auth screens don't feel flat.
        // Tokenized: accent + accent-2 mix to whatever palette is loaded.
        backgroundImage:
          'radial-gradient(900px 480px at 88% -10%, color-mix(in oklab, var(--accent-2, var(--accent)) 20%, transparent), transparent 60%), radial-gradient(700px 400px at 12% 110%, color-mix(in oklab, var(--accent) 14%, transparent), transparent 60%)',
      }}
    >
      <header className="relative z-10 flex items-center justify-between px-6 py-5 md:px-8">
        <Link
          to="/"
          // The marketing site lives on a different origin (apex), but
          // /login etc are on app.* — surface this via the wordmark anchor.
          className="inline-flex items-center gap-2 text-text"
        >
          <BrandMark />
          <span className="hidden font-display text-base font-semibold tracking-tight sm:inline">
            pozyskajpacjenta.pl
          </span>
        </Link>
        <a
          href="https://pozyskajpacjenta.pl/pomoc"
          className="text-xs font-medium text-text-muted hover:text-text"
        >
          Pomoc
        </a>
      </header>

      <main className="relative z-10 grid place-items-center px-6 py-4">
        {children}
      </main>

      <footer className="relative z-10 px-6 pb-6 text-center text-xs text-text-subtle md:px-8">
        {smallPrint ?? (
          <>
            © pozyskajpacjenta.pl ·{' '}
            <a
              href="https://pozyskajpacjenta.pl/polityka-prywatnosci"
              className="underline-offset-2 hover:underline"
            >
              Polityka prywatności
            </a>{' '}
            ·{' '}
            <a
              href="https://pozyskajpacjenta.pl/regulamin"
              className="underline-offset-2 hover:underline"
            >
              Regulamin
            </a>
          </>
        )}
      </footer>
    </div>
  );
}

/**
 * BrandMark — square chip with the platform initial. Mirrors the
 * collapsed-sidebar brand chip so the visual identity stays consistent
 * across surfaces.
 */
function BrandMark() {
  return (
    <span
      aria-hidden="true"
      className="grid size-8 place-items-center rounded-md bg-accent font-display text-sm font-semibold text-text-inverted shadow-[0_2px_8px_-2px_color-mix(in_oklab,var(--accent)_60%,transparent)]"
    >
      pp
    </span>
  );
}

/**
 * PasswordStrengthMeter — 4-bar visual indicator that reacts to length +
 * character-class diversity. Heuristic only, never gated on (don't block
 * the submit on strength, only show the meter so the user understands
 * what makes a strong password).
 *
 * Scoring (max 4):
 *   +1 if length >= 8
 *   +1 if length >= 12
 *   +1 if mix of letters + digits
 *   +1 if any symbol char
 */
export function PasswordStrengthMeter({ value }: { value: string }) {
  const score = scorePassword(value);
  const labels = ['za krótkie', 'słabe', 'średnie', 'mocne', 'bardzo mocne'];
  const colors = [
    'bg-border',
    'bg-error',
    'bg-warning',
    'bg-accent',
    'bg-success',
  ];
  return (
    <div className="space-y-1">
      <div className="flex gap-1.5" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors',
              i < score ? colors[score] ?? 'bg-success' : 'bg-border'
            )}
          />
        ))}
      </div>
      <p className="text-xs text-text-subtle">
        Siła hasła: <span className="font-medium text-text-muted">{labels[score]}</span>
      </p>
    </div>
  );
}

function scorePassword(v: string): number {
  if (!v) return 0;
  let s = 0;
  if (v.length >= 8) s++;
  if (v.length >= 12) s++;
  if (/[a-zA-Z]/.test(v) && /\d/.test(v)) s++;
  if (/[^a-zA-Z0-9]/.test(v)) s++;
  return s;
}
